import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import axios from "axios";
import checkAuthz from "../../../util/authz";
import CheckUnauth from "../../auth/CheckUnauth";

const DashboardContainer = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Section = styled.div`
  margin-bottom: 30px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
  border-bottom: 2px solid #4caf50;
  padding-bottom: 10px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const ReportDropdown = styled.select`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
  &:hover {
    border-color: #4caf50;
  }
  &:focus {
    outline: none;
    border-color: #4caf50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
  }
`;

const DropdownOption = styled.option`
  padding: 10px;
`;

const ReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 10px;
`;

const ReportTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 18px;
`;

const ReportDate = styled.div`
  color: #666;
  font-size: 14px;
`;

const ReportStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 10px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
`;

const StatValue = styled.span`
  font-size: 16px;
  font-weight: bold;
  color: #333;
`;

const ReportDetails = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ddd;
`;

const RestaurantList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 15px;
`;

const RestaurantCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
  background: #fafafa;
`;

const RestaurantHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 10px;
`;

const RestaurantName = styled.h4`
  margin: 0;
  color: #333;
  font-size: 16px;
`;

const RestaurantStatus = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  ${(props) => {
    if (props.status === "success") return "background: #c8e6c9; color: #2e7d32;";
    if (props.status === "no_vegan_options") return "background: #fff3e0; color: #e65100;";
    if (props.status === "processing") return "background: #e3f2fd; color: #1976d2;";
    if (props.status === "pending") return "background: #f5f5f5; color: #666;";
    return "background: #ffcdd2; color: #c62828;";
  }}
`;

const RestaurantDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  margin-top: 10px;
  font-size: 14px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLabel = styled.span`
  color: #666;
  font-size: 12px;
  margin-bottom: 4px;
`;

const DetailValue = styled.span`
  color: #333;
`;

const LinkList = styled.div`
  margin-top: 10px;
  max-height: 150px;
  overflow-y: auto;
`;

const LinkItem = styled.a`
  display: block;
  color: #1976d2;
  text-decoration: none;
  font-size: 12px;
  padding: 4px 0;
  &:hover {
    text-decoration: underline;
  }
`;

const VeganOptionsList = styled.div`
  margin-top: 10px;
  max-height: 200px;
  overflow-y: auto;
`;

const VeganOptionItem = styled.div`
  padding: 8px;
  margin-bottom: 8px;
  background: white;
  border-left: 3px solid #4caf50;
  border-radius: 4px;
`;

const OptionName = styled.div`
  font-weight: bold;
  color: #333;
  margin-bottom: 4px;
`;

const OptionDescription = styled.div`
  color: #666;
  font-size: 12px;
`;

const AdHocSearchReports = ({ token, location }) => {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(function loadReportsOnMount() {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const config = {
        headers: {
          "x-auth-token": token,
        },
      };
      const res = await axios.get("/api/ad-hoc-reports", config);
      const reportsData = res.data.reports || [];
      console.log("[AdHocSearchReports] Loaded reports:", reportsData.length);
      console.log("[AdHocSearchReports] First report sample:", reportsData[0]);
      setReports(reportsData);
    } catch (err) {
      console.error("Error loading reports:", err);
      setError(err.response?.data?.error || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const loadReportDetails = async (reportId) => {
    try {
      const config = {
        headers: {
          "x-auth-token": token,
        },
      };
      const res = await axios.get(`/api/ad-hoc-reports/${reportId}`, config);
      setSelectedReport(res.data);
    } catch (err) {
      console.error("Error loading report details:", err);
      setError(err.response?.data?.error || "Failed to load report details");
    }
  };

  const formatDuration = (ms) => {
    if (ms === null || ms === undefined || isNaN(ms)) return "N/A";
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) return "Invalid Date";
      return dateObj.toLocaleString();
    } catch (error) {
      return "Invalid Date";
    }
  };

  if (loading && reports.length === 0) {
    return (
      <DashboardContainer>
        <CheckUnauth pathname={location?.pathname || "/admin"} />
        <LoadingMessage>Loading reports...</LoadingMessage>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <CheckUnauth pathname={location?.pathname || "/admin"} />
      <h1>Ad Hoc Search Reports</h1>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Section>
        <SectionTitle>Search Reports</SectionTitle>
        {reports.length === 0 ? (
          <div>No reports found.</div>
        ) : (
          <div>
            <label htmlFor="report-select" style={{ display: "block", marginBottom: "10px", fontWeight: "bold", color: "#333" }}>
              Select a Report ({reports.length} total):
            </label>
            <ReportDropdown
              id="report-select"
              value={selectedReport?.jobId || ""}
              onChange={(e) => {
                const selectedJobId = e.target.value;
                if (selectedJobId === "") {
                  setSelectedReport(null);
                } else {
                  const report = reports.find((r) => r.jobId === selectedJobId);
                  if (report) {
                    // If we already have full details for this exact report, no need to reload
                    if (selectedReport?.jobId === report.jobId && selectedReport.restaurants) {
                      return;
                    }
                    // Load full details for the selected report
                    loadReportDetails(report.jobId);
                  }
                }
              }}
            >
              <DropdownOption value="">-- Select a report --</DropdownOption>
              {reports.map((report, index) => {
                if (!report) {
                  console.warn(`[AdHocSearchReports] Report at index ${index} is null/undefined`);
                  return null;
                }
                
                if (!report.jobId) {
                  console.warn(`[AdHocSearchReports] Report at index ${index} missing jobId:`, report);
                  return null;
                }
                
                // Default to "completed" if jobStatus is missing (for old reports)
                const jobStatus = report.jobStatus || "completed";
                const statusLabel = `[${jobStatus.toUpperCase()}]`;
                
                const locationLabel = report.searchLocation && 
                  typeof report.searchLocation.lat === 'number' && 
                  typeof report.searchLocation.lng === 'number'
                  ? `(${report.searchLocation.lat.toFixed(4)}, ${report.searchLocation.lng.toFixed(4)})`
                  : "(N/A, N/A)";
                
                // Show "Ongoing" for active jobs, otherwise show endTime (when search completed)
                // endTime is more meaningful than createdAt (when report was created)
                let dateLabel;
                if (jobStatus === "processing" || jobStatus === "pending") {
                  dateLabel = "Ongoing";
                } else if (report.endTime) {
                  dateLabel = formatDate(report.endTime);
                } else if (report.updatedAt) {
                  dateLabel = formatDate(report.updatedAt);
                } else {
                  dateLabel = "Unknown date";
                }
                
                const processed = report.restaurantsProcessed ?? 0;
                const veganResults = report.veganResultsFound ?? 0;
                const summaryLabel = `${processed} processed, ${veganResults} vegan results`;
                
                const userLabel = report.userEmail || "Unknown user";

                return (
                  <DropdownOption key={report.jobId || report._id || index} value={report.jobId}>
                    {statusLabel} {userLabel} - {locationLabel} - {dateLabel} - {summaryLabel}
                  </DropdownOption>
                );
              })}
            </ReportDropdown>
            
            {selectedReport && (
              <div style={{ marginTop: "20px", padding: "15px", background: "#f9f9f9", borderRadius: "8px" }}>
                <ReportStats>
                  <StatItem>
                    <StatLabel>Google Restaurants</StatLabel>
                    <StatValue>{selectedReport.googleRestaurantsCount ?? 0}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Processed</StatLabel>
                    <StatValue>{selectedReport.restaurantsProcessed ?? 0}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Vegan Results</StatLabel>
                    <StatValue>{selectedReport.veganResultsFound ?? 0}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Duration</StatLabel>
                    <StatValue>{selectedReport.duration ? formatDuration(selectedReport.duration) : "N/A"}</StatValue>
                  </StatItem>
                  {selectedReport.jobStatus && (
                    <StatItem>
                      <StatLabel>Status</StatLabel>
                      <StatValue style={{ 
                        textTransform: "capitalize",
                        color: selectedReport.jobStatus === "completed" ? "#2e7d32" :
                               selectedReport.jobStatus === "processing" ? "#1976d2" :
                               selectedReport.jobStatus === "failed" ? "#c62828" :
                               selectedReport.jobStatus === "cancelled" ? "#666" : "#333"
                      }}>
                        {selectedReport.jobStatus}
                      </StatValue>
                    </StatItem>
                  )}
                </ReportStats>
              </div>
            )}
          </div>
        )}
      </Section>

      {selectedReport && (
        <Section>
          <SectionTitle>Report Details</SectionTitle>
          <ReportDetails>
            <ReportStats>
              <StatItem>
                <StatLabel>User</StatLabel>
                <StatValue>{selectedReport.userEmail || "Unknown"}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Search Location</StatLabel>
                <StatValue>
                  {selectedReport.searchLocation &&
                   typeof selectedReport.searchLocation.lat === 'number' &&
                   typeof selectedReport.searchLocation.lng === 'number'
                    ? `(${selectedReport.searchLocation.lat.toFixed(4)}, ${selectedReport.searchLocation.lng.toFixed(4)})${selectedReport.searchLocation.radius ? ` - ${selectedReport.searchLocation.radius}km radius` : ''}`
                    : "N/A"}
                </StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Start Time</StatLabel>
                <StatValue>{selectedReport.startTime ? formatDate(selectedReport.startTime) : "N/A"}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>End Time</StatLabel>
                <StatValue>{selectedReport.endTime ? formatDate(selectedReport.endTime) : "N/A"}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Total Duration</StatLabel>
                <StatValue>{selectedReport.duration ? formatDuration(selectedReport.duration) : "N/A"}</StatValue>
              </StatItem>
            </ReportStats>

            <RestaurantList>
              <h3 style={{ marginTop: "20px", marginBottom: "10px" }}>
                Restaurants ({selectedReport.restaurants && Array.isArray(selectedReport.restaurants) ? selectedReport.restaurants.length : 0})
              </h3>
              {selectedReport.restaurants && Array.isArray(selectedReport.restaurants) && selectedReport.restaurants.map((restaurant, index) => (
                <RestaurantCard key={index}>
                  <RestaurantHeader>
                    <RestaurantName>{restaurant.name}</RestaurantName>
                    <RestaurantStatus status={restaurant.status}>
                      {restaurant.status === "success"
                        ? "Success"
                        : restaurant.status === "no_vegan_options"
                        ? "No Vegan Options"
                        : restaurant.status === "processing"
                        ? "Processing"
                        : restaurant.status === "pending"
                        ? "Pending"
                        : "Failed"}
                    </RestaurantStatus>
                  </RestaurantHeader>
                  <RestaurantDetails>
                    <DetailItem>
                      <DetailLabel>URL</DetailLabel>
                      <DetailValue>
                        <a
                          href={restaurant.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {restaurant.url}
                        </a>
                      </DetailValue>
                    </DetailItem>
                    {restaurant.timing && (
                      <>
                        {restaurant.timing.jobCreatedAt && (
                          <DetailItem>
                            <DetailLabel>Job Created</DetailLabel>
                            <DetailValue>
                              {formatDate(restaurant.timing.jobCreatedAt)}
                            </DetailValue>
                          </DetailItem>
                        )}
                        {restaurant.timing.resultReceivedAt && (
                          <DetailItem>
                            <DetailLabel>Result Received</DetailLabel>
                            <DetailValue>
                              {formatDate(restaurant.timing.resultReceivedAt)}
                            </DetailValue>
                          </DetailItem>
                        )}
                        {restaurant.timing.duration !== null && restaurant.timing.duration !== undefined && (
                          <DetailItem>
                            <DetailLabel>Processing Duration</DetailLabel>
                            <DetailValue>
                              {formatDuration(restaurant.timing.duration)}
                            </DetailValue>
                          </DetailItem>
                        )}
                        {(!restaurant.timing.resultReceivedAt || !restaurant.timing.duration) && (
                          <DetailItem>
                            <DetailLabel>Status</DetailLabel>
                            <DetailValue>
                              {restaurant.status === "processing" ? "Still processing..." : 
                               restaurant.status === "pending" ? "Waiting to start..." : 
                               "Processing..."}
                            </DetailValue>
                          </DetailItem>
                        )}
                      </>
                    )}
                    {restaurant.menuLinks && (
                      <>
                        <DetailItem>
                          <DetailLabel>Menu Links Found</DetailLabel>
                          <DetailValue>
                            {restaurant.menuLinks.found}
                          </DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Total Items Extracted</DetailLabel>
                          <DetailValue>
                            {restaurant.menuLinks.totalItemsExtracted}
                          </DetailValue>
                        </DetailItem>
                      </>
                    )}
                    {restaurant.urlExtractionSuccess !== undefined && (
                      <DetailItem>
                        <DetailLabel>URL Extracted from HTML</DetailLabel>
                        <DetailValue>
                          {restaurant.urlExtractionSuccess ? "Yes" : "No"}
                        </DetailValue>
                      </DetailItem>
                    )}
                    {restaurant.veganOptions && (
                      <>
                        <DetailItem>
                          <DetailLabel>Guaranteed Vegan</DetailLabel>
                          <DetailValue>
                            {restaurant.veganOptions.guaranteedVeganCount}
                          </DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Probably Vegan</DetailLabel>
                          <DetailValue>
                            {restaurant.veganOptions.probablyVeganCount}
                          </DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Maybe Vegan</DetailLabel>
                          <DetailValue>
                            {restaurant.veganOptions.maybeVeganCount}
                          </DetailValue>
                        </DetailItem>
                        <DetailItem>
                          <DetailLabel>Probably Not Vegan</DetailLabel>
                          <DetailValue>
                            {restaurant.veganOptions.probablyNotVeganCount}
                          </DetailValue>
                        </DetailItem>
                      </>
                    )}
                  </RestaurantDetails>
                  {restaurant.menuLinks?.links?.length > 0 && (
                    <div style={{ marginTop: "15px" }}>
                      <DetailLabel>Menu Links Traversed:</DetailLabel>
                      <LinkList>
                        {restaurant.menuLinks.links.map((link, linkIndex) => (
                          <LinkItem
                            key={linkIndex}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {link}
                          </LinkItem>
                        ))}
                      </LinkList>
                    </div>
                  )}
                  {restaurant.menuLinks?.contributingUrls?.length > 0 && (
                    <div style={{ marginTop: "15px" }}>
                      <DetailLabel>Menu URLs That Contributed Items:</DetailLabel>
                      <LinkList>
                        {restaurant.menuLinks.contributingUrls.map((link, linkIndex) => (
                          <LinkItem
                            key={linkIndex}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {link}
                          </LinkItem>
                        ))}
                      </LinkList>
                    </div>
                  )}
                  {restaurant.veganOptions?.veganOptionsList?.length > 0 && (
                    <div style={{ marginTop: "15px" }}>
                      <DetailLabel>Vegan Options Found:</DetailLabel>
                      <VeganOptionsList>
                        {restaurant.veganOptions.veganOptionsList.map(
                          (option, optIndex) => (
                            <VeganOptionItem key={optIndex}>
                              <OptionName>{option.option}</OptionName>
                              {option.description && (
                                <OptionDescription>
                                  {option.description}
                                </OptionDescription>
                              )}
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#999",
                                  marginTop: "4px",
                                }}
                              >
                                {option.category} (
                                {(option.probability * 100).toFixed(0)}%)
                              </div>
                            </VeganOptionItem>
                          )
                        )}
                      </VeganOptionsList>
                    </div>
                  )}
                  {restaurant.error && (
                    <div
                      style={{
                        marginTop: "10px",
                        padding: "10px",
                        background: "#ffebee",
                        color: "#c62828",
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    >
                      Error: {restaurant.error}
                    </div>
                  )}
                </RestaurantCard>
              ))}
            </RestaurantList>
          </ReportDetails>
        </Section>
      )}
    </DashboardContainer>
  );
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
});

export default connect(mapStateToProps)(AdHocSearchReports);

